// GENERAL
import React, { useState, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";

// COMPONENTS
import { BaseText, BaseContainer } from "../../../../../../components";
import { BundleFee, BundleItem, BundleHeader } from "../../index";

// UTILS
import { formatCurrency } from "../../../../../../utils";
import getPlaceholderImage from "../../helpers/getPlaceholderImage";
import getImageSrc from "../../helpers/getImageSrc";
// TYPES
import { OrderBundleProps, BundleItemTypes } from "../../types";

// ASSETS
import { rightAngleSvg } from "../../../../../../assets";

const NestedBundle: React.FC<OrderBundleProps> = ({
	orderBundle,
	fields,
	hostName,
}) => {
	const { quantity, childBundle, isRequired } = orderBundle;
	const {
		bundleItems,
		name,
		number,
		uuid,
		_bundleUnitPrice,
		_bundleLeasePrice,
	} = childBundle;
	const [expandedItems, setExpandedItems] = useState<string[]>([]);
	const [hoveredItemId, setHoveredItemId] = useState<string | null>(null);

	const toggleExpand = (id: string) => {
		setExpandedItems((prev) => {
			const isExpanded = prev.includes(id);
			return isExpanded
				? prev.filter((item) => item !== id)
				: [...prev, id];
		});
	};

	const renderBundlePrimaryItemPrice = (bundleItem: BundleItemTypes) => {
		return formatCurrency(bundleItem.price as number);
	};

	const renderBundleItemPrice = (bundleItem: any) => {
		return formatCurrency(bundleItem.price);
	};

	///// EXTRACTING DATA /////
	const primaryItem = useMemo(() => {
		return bundleItems?.find(
			(bundleItem: BundleItemTypes) =>
				bundleItem?.originalBundleItemGroup.slug === "primary"
		);
	}, [orderBundle.bundleItems]);

	const primaryItemQuantity = primaryItem?.quantity;
	const primaryPartNumberValue = primaryItem?.item.partNumber;
	const primaryTitleValue = primaryItem?.item.title;
	const primaryImageSrc = getImageSrc(
		primaryItem?.bundleItem.item.itemImages as BundleItemTypes,
		getPlaceholderImage(hostName)
	);
	const primaryItemGroupSlug = primaryItem?.bundleItem?.bundleItemGroup.slug;
	const primaryIsRequired = primaryItem?.bundleItem.isRequired;
	const primaryItemPrice = renderBundlePrimaryItemPrice(
		primaryItem as BundleItemTypes
	);

	const secondaryAndTertiaryBundleItems = useMemo(() => {
		return [
			...bundleItems.filter((bundleItem: BundleItemTypes) => {
				return bundleItem?.originalBundleItemGroup.slug === "secondary";
			}),
			...bundleItems.filter(
				(bundleItem: BundleItemTypes) =>
					bundleItem?.originalBundleItemGroup.slug === "tertiary" &&
					bundleItem?.bundleItem?.item.assetType.name !== "FEE"
			),
		];
	}, [bundleItems]);

	const fees = useMemo(() => {
		return bundleItems.filter(
			(bundleItem: BundleItemTypes) =>
				bundleItem?.bundleItem?.item.assetType.name === "FEE"
		);
	}, [bundleItems]);

	///// CLASS NAMES /////
	const bundleContainerClasses = `pr-2.5 pb-0 rounded-lg border-transparent`;
	const parentContainerClasses = `border-1 rounded-xl transition-all duration-200 w-full mb-2.5  ${
		hoveredItemId ? "border-secondary-800" : "border-transparent"
	} group-hover:border-secondary-800`;

	///// TEXT FROM FIELDS /////
	const bundleContentsHeading = fields.orderItems.headings[1].label;
	const additionalFeesHeading = fields.orderItems.headings[2].label;
	const quantityText = fields.orderItems.bundleDetails.quantity.prefix;
	const currencySymbol = fields.orderItems.settings.currencyCharacter;
	const bundlePrefix = fields.orderItems.bundleDetails.bundleTitle.prefix;
	const bundleBadgeText = fields.orderItems.bundleDetails.bundleBadge.label;
	const leaseBadgeText = fields.orderItems.bundleDetails.leaseBadge.label;
	const addOnBadgeText = fields.orderItems.bundleDetails.addOnBadge.label;
	const requiredBadgeText =
		fields.orderItems.bundleDetails.requiredBadge.label;
	const leaseSuffix = fields.orderItems.bundleDetails.leasePrice.suffix;
	const partNumberPrefix = fields.orderItems.bundleDetails.partNumber.prefix;
	const bundleNumberLabel = fields.orderItems.bundleDetails.kitId.label;
	const trackingNumberPrefix =
		fields.orderItems.bundleDetails.trackingNumber.label;

	// SETTINGS
	const areFeesVisible = fields.orderItems.headings[2].isVisible;
	const kitIdVisible = fields.orderItems.bundleDetails.kitId.isVisible;

	return (
		<div className={parentContainerClasses}>
			<div className="flex">
				<div className="flex justify-center items-start pt-2.5 w-[64px] pl-5 mr-4 max-sm:pl-2 max-sm:mr-2">
					<img src={rightAngleSvg} alt="" className="size-12" />
				</div>
				<div
					key={uuid}
					className="relative w-full py-3 hover:cursor-pointer group"
					onClick={() => toggleExpand(uuid)}
					onMouseEnter={() => setHoveredItemId(uuid)}
					onMouseLeave={() => setHoveredItemId(null)}
					aria-expanded={expandedItems.includes(uuid)}
				>
					<BaseContainer
						columnContainerClasses={bundleContainerClasses}
						columnShape="semi-rounded"
					>
						<div className="flex justify-between items-center w-full">
							<BundleHeader
								hoveredItemId={hoveredItemId}
								expandedItems={expandedItems}
								uuid={uuid}
								extPriceBundleLease={_bundleLeasePrice}
								extPriceBundlePurchase={_bundleUnitPrice}
								quantity={quantity}
								name={name}
								number={number}
								quantityText={quantityText}
								leaseBadgeText={leaseBadgeText}
								leaseSuffix={leaseSuffix}
								currencySymbol={currencySymbol}
								bundlePrefix={bundlePrefix}
								bundleBadgeText={bundleBadgeText}
								kitIdVisible={kitIdVisible}
								kitIdPrefix={bundleNumberLabel}
								isNestedBundle={true}
								isRequired={isRequired}
							/>
						</div>
						<AnimatePresence></AnimatePresence>
					</BaseContainer>
				</div>
			</div>

			{expandedItems.includes(uuid) && (
				<motion.div
					initial={{ opacity: 0, height: 0 }}
					animate={{ opacity: 1, height: "auto" }}
					exit={{ opacity: 0, height: 0 }}
					transition={{ duration: 0.3, ease: "easeInOut" }}
					className="flex flex-col items-start mt-1 pl-6"
				>
					<BaseText
						color="text-gray-700"
						fontFamily="font-inter-medium font-medium"
						tag="p"
						text={bundleContentsHeading}
						size="text-base"
						additionalClasses="pb-2.5 ml-[138px]"
					/>
					<div className="ml-[60px]">
						{primaryItem && (
							<div className="flex">
								<div className="flex justify-center items-start w-[64px] pl-5 mr-4 max-sm:pl-2 max-sm:mr-2">
									<img
										src={rightAngleSvg}
										alt=""
										className="size-12"
									/>
								</div>
								<BundleItem
									key={uuid}
									item={primaryItem}
									imageSrc={primaryImageSrc}
									leaseSuffix={leaseSuffix}
									leaseBadgeText={leaseBadgeText}
									currencySymbol={currencySymbol}
									quantityText={quantityText}
									addOnBadgeText={addOnBadgeText}
									requiredBadgeText={requiredBadgeText}
									partNumberPrefix={partNumberPrefix}
									trackingNumberPrefix={trackingNumberPrefix}
									quantityValue={primaryItemQuantity}
									partNumberValue={primaryPartNumberValue}
									titleValue={primaryTitleValue}
									bundleItemGroupSlug={primaryItemGroupSlug}
									isRequired={true}
									price={primaryItemPrice}
									isNestedBundleItem={true}
								/>
							</div>
						)}
						{secondaryAndTertiaryBundleItems.map(
							(bundleItem: BundleItemTypes, index: number) => {
								return (
									<div
										className="flex"
										key={`${bundleItem.uuid}-${index}`}
									>
										<div className="flex justify-center items-start w-[64px] pl-5 mr-4 max-sm:pl-2 max-sm:mr-2">
											<img
												src={rightAngleSvg}
												alt=""
												className="size-12"
											/>
										</div>
										<BundleItem
											key={bundleItem.uuid}
											item={bundleItem}
											leaseSuffix={leaseSuffix}
											leaseBadgeText={leaseBadgeText}
											currencySymbol={currencySymbol}
											quantityText={quantityText}
											addOnBadgeText={addOnBadgeText}
											requiredBadgeText={
												requiredBadgeText
											}
											partNumberPrefix={partNumberPrefix}
											trackingNumberPrefix={
												trackingNumberPrefix
											}
											quantityValue={bundleItem.quantity}
											partNumberValue={
												bundleItem.item.partNumber
											}
											titleValue={bundleItem.item.title}
											bundleItemGroupSlug={
												bundleItem
													.originalBundleItemGroup
													.slug
											}
											isRequired={
												bundleItem.bundleItem.isRequired
											}
											price={renderBundleItemPrice(
												bundleItem
											)}
											imageSrc={getImageSrc(
												bundleItem.bundleItem.item
													.itemImages,
												getPlaceholderImage(hostName)
											)}
											isNestedBundleItem={true}
										/>
									</div>
								);
							}
						)}

						{fees.length > 0 && areFeesVisible && (
							<div className="flex flex-col pl-1">
								<div className="flex justify-start">
									<BaseText
										color="text-navy-500"
										fontFamily="font-medium"
										tag="h3"
										text={additionalFeesHeading}
										size="text-base"
										additionalClasses="mt-2.5"
									/>
								</div>

								{fees.map(
									(item: BundleItemTypes, index: number) => (
										<div className="flex">
											<div className="flex justify-center items-start w-[64px] pl-5 mr-4 max-sm:pl-2 max-sm:mr-2">
												<img
													src={rightAngleSvg}
													alt=""
													className="size-12"
												/>
											</div>
											<BundleFee
												bundleFee={item}
												key={`${item.uuid}-${index}`}
												leaseSuffix={leaseSuffix}
												currencySymbol={currencySymbol}
												partNumberPrefix={
													partNumberPrefix
												}
											/>
										</div>
									)
								)}
							</div>
						)}
					</div>
				</motion.div>
			)}
		</div>
	);
};

export default NestedBundle;
